import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';

import type { FollowUpDue } from '../api/endpoints';
import type { FollowUp } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { bucketOf, buckets, dueInfo, initials } from '../ops/ticketView';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

const dueFilters: { value: FollowUpDue; label: string }[] = [
  { value: 'overdue', label: 'Überfällig' },
  { value: 'today', label: 'Heute' },
  { value: 'upcoming', label: 'Später' },
  { value: 'all', label: 'Alle offenen' },
];

// FollowUpsPage: Wiedervorlagen und Rückrufe – eigene (Telefonakquise) oder projektweit (Support).
// Eingebettet im Posteingang (Filter Gebiet/Makler) und in der Akquise (eigene).
export function FollowUpsPage({
  embedded = false,
  defaultAssigned = 'me',
  areaId,
  fieldSalesUserId,
}: {
  embedded?: boolean;
  defaultAssigned?: 'me' | 'all';
  areaId?: string;
  fieldSalesUserId?: string;
}) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const due = (params.get('due') as FollowUpDue | null) ?? 'all';
  const assigned = params.get('assigned') ?? defaultAssigned;
  const filters = { due, assigned, area_id: areaId, field_sales_user_id: fieldSalesUserId };
  const list = useQuery({
    queryKey: projectKeys.followUps(project.id, filters),
    queryFn: ({ signal }) => papi.followUps(filters, signal),
    refetchInterval: 60_000,
  });
  const complete = useMutation({
    mutationFn: (id: string) => papi.completeFollowUp(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) }),
  });
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next);
  };

  return (
    <div className={embedded ? 'embedded-page' : 'page'}>
      {!embedded && (
        <header className="page-header">
          <h1>Wiedervorlagen</h1>
        </header>
      )}
      <div className="chips" role="group" aria-label="Fälligkeit">
        {dueFilters.map((d) => (
          <button key={d.value} type="button" className={due === d.value ? 'chip active' : 'chip'} aria-pressed={due === d.value} onClick={() => set('due', d.value)}>
            {d.label}
          </button>
        ))}
        <span className="topbar-spacer" />
        <button type="button" className={assigned === 'me' ? 'chip active' : 'chip'} aria-pressed={assigned === 'me'} onClick={() => set('assigned', 'me')}>
          Meine
        </button>
        <button type="button" className={assigned === 'all' ? 'chip active' : 'chip'} aria-pressed={assigned === 'all'} onClick={() => set('assigned', 'all')}>
          Projektweit
        </button>
      </div>
      {complete.isError && <MutationError error={complete.error} />}
      {list.isPending ? (
        <PageLoader />
      ) : list.isError ? (
        <ErrorMessage error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState title="Keine Wiedervorlagen" icon="clock">
          Für diese Auswahl ist nichts offen. Neue Rückrufe und Wiedervorlagen entstehen nach Gesprächen in der Akquise.
        </EmptyState>
      ) : (
        <div className="task-groups">
          {groupFollowUps(list.data.items, due === 'all').map((g) => (
            <section key={g.key} className="task-group" aria-label={g.label || 'Wiedervorlagen'}>
              {g.label && (
                <h3 className={`task-group-title tone-${g.tone}`}>
                  {g.label} <span className="task-group-count">{g.items.length}</span>
                </h3>
              )}
              <ul className="task-cards">
                {g.items.map((f) => {
                  const d = dueInfo({ due_at: f.due_at, overdue: f.overdue, status: 'OPEN' });
                  const kind = f.type === 'CALLBACK' ? 'Rückruf' : 'Wiedervorlage';
                  return (
                    <li key={f.id} className={f.overdue ? 'task-card prio-high' : 'task-card'}>
                      <span className="task-icon" aria-hidden="true">
                        <Icon name={f.type === 'CALLBACK' ? 'phone' : 'clock'} size={18} />
                      </span>
                      <div className="task-main">
                        {can('call.start') ? (
                          <Link className="task-title" to={`/p/${project.key}/akquise/${f.lead_id}`}>
                            {f.lead_name}
                          </Link>
                        ) : (
                          <span className="task-title">{f.lead_name}</span>
                        )}
                        <p className="task-preview">{f.reason || (f.type === 'CALLBACK' ? 'Vereinbarter Rückruf' : 'Erneut anrufen')}</p>
                        <p className="task-meta">
                          <span className="tag-chip">{kind}</span>
                          {f.lead_city && (
                            <span className="meta-item">
                              <Icon name="pin" size={14} />
                              {f.lead_city}
                            </span>
                          )}
                          {f.lead_phone && (
                            <span className="meta-item">
                              <Icon name="phone" size={14} />
                              {f.lead_phone}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="task-side">
                        {d && (
                          <span className={`due due-${d.tone}`} title={fmtDateTime(f.due_at)}>
                            <Icon name="clock" size={14} />
                            {d.label}
                          </span>
                        )}
                        {f.assigned_user_name && (
                          <span className="assignee">
                            <span className="avatar" aria-hidden="true">
                              {initials(f.assigned_user_name)}
                            </span>
                            {f.assigned_user_name}
                          </span>
                        )}
                        {can('followup.complete') && (
                          <button type="button" className="btn btn-small task-action" disabled={complete.isPending} onClick={() => complete.mutate(f.id)}>
                            Erledigt
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// groupFollowUps: bei „Alle offenen“ in Gruppen nach Fälligkeit (überfällig, heute, nächste 7 Tage, später).
function groupFollowUps(items: FollowUp[], grouped: boolean) {
  if (!grouped) return [{ key: 'all', label: '', tone: 'muted', items }];
  return buckets
    .map((b) => ({ ...b, items: items.filter((f) => bucketOf({ due_at: f.due_at, overdue: f.overdue, status: 'OPEN' }) === b.key) }))
    .filter((g) => g.items.length > 0);
}
